import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography, 
  Box, 
  IconButton,
  Zoom
} from '@mui/material';
import { 
  ErrorOutline as ErrorIcon, 
  Close as CloseIcon,
  WarningAmber as WarningIcon,
  InfoOutlined as InfoIcon
} from '@mui/icons-material';

const MessagePopup = () => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [title, setTitle] = useState('Error');
  const [type, setType] = useState('error');
  const [onCloseCallback, setOnCloseCallback] = useState(null);

  useEffect(() => {
    const handleShowPopup = (event) => {
      const { message, title, type, onClose } = event.detail;
      setMessage(message);
      setTitle(title || 'Error');
      setType(type || 'error');
      setOnCloseCallback(() => onClose);
      setOpen(true);
    };

    window.addEventListener('show-message-popup', handleShowPopup);
    return () => window.removeEventListener('show-message-popup', handleShowPopup);
  }, []);

  const handleClose = () => {
    setOpen(false);
    if (onCloseCallback) {
      onCloseCallback();
      setOnCloseCallback(null);
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'warning': return <WarningIcon sx={{ fontSize: 50, color: '#ff9800' }} />;
      case 'info': return <InfoIcon sx={{ fontSize: 50, color: '#2196f3' }} />;
      default: return <ErrorIcon sx={{ fontSize: 50, color: '#f44336' }} />;
    }
  };

  const getHeaderColor = () => {
    switch (type) {
      case 'warning': return '#ff9800';
      case 'info': return '#2196f3';
      default: return '#f44336';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      TransitionComponent={Zoom}
      keepMounted
      PaperProps={{
        sx: {
          borderRadius: '16px',
          background: 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(12px)',
          boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.37)',
          border: '1px solid rgba(255, 255, 255, 0.18)',
          minWidth: '400px',
          maxWidth: '500px',
        }
      }}
    >
      <Box sx={{ 
        height: '6px', 
        width: '100%', 
        backgroundColor: getHeaderColor(),
        borderTopLeftRadius: '16px',
        borderTopRightRadius: '16px'
      }} />
      
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 12,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
        <Box sx={{ mb: 2 }}>
          {getIcon()}
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1, color: '#1a1a1a' }}>
          {title}
        </Typography>
        <Typography variant="body1" sx={{ color: '#4a4a4a', lineHeight: 1.6 }}>
          {message}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'center', pb: 3, px: 3 }}>
        <Button 
          onClick={handleClose} 
          variant="contained" 
          sx={{ 
            borderRadius: '10px',
            px: 4,
            py: 1,
            textTransform: 'none',
            fontSize: '1rem',
            fontWeight: 600,
            backgroundColor: getHeaderColor(),
            '&:hover': {
              backgroundColor: getHeaderColor(),
              filter: 'brightness(0.9)',
              boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
            },
            transition: 'all 0.2s'
          }}
        >
          Dismiss
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MessagePopup;

export const showErrorPopup = (message, title = 'Error', onClose = null) => {
  window.dispatchEvent(new CustomEvent('show-message-popup', { 
    detail: { message, title, type: 'error', onClose } 
  }));
};


